import type { AppErrorEvent } from '../services/appErrorMonitorService';

export type CrashEventKind = 'js_fatal' | 'dirty_exit' | 'restart_app' | 'restart_suppressed' | 'unknown';

export type CrashGroup = {
  fingerprint: string;
  eventKind: CrashEventKind;
  message: string;
  lastScreen: string | null;
  source: string | null;
  platforms: string[];
  versions: string[];
  count: number;
  lastSeenAt: string;
  firstSeenAt: string;
  sample: AppErrorEvent;
};

const CRASH_SOURCES = new Set([
  'ErrorUtils',
  'unhandledrejection',
  'root-error-boundary',
]);

const readTagOrContext = (event: AppErrorEvent, key: string): unknown => {
  const fromTags = event.tags?.[key];
  if (fromTags !== undefined && fromTags !== null) return fromTags;
  return event.context?.[key];
};

export const getCrashEventKind = (event: AppErrorEvent): CrashEventKind => {
  const tagged = readTagOrContext(event, 'eventKind');
  if (
    tagged === 'js_fatal' ||
    tagged === 'dirty_exit' ||
    tagged === 'restart_app' ||
    tagged === 'restart_suppressed'
  ) {
    return tagged;
  }

  const source = event.source ?? '';
  if (source === 'ErrorUtils' || source === 'root-error-boundary') return 'js_fatal';
  if (source.includes('dirty-exit') || source.includes('dirty_exit')) return 'dirty_exit';
  if (source.includes('restart-app')) return 'restart_app';
  if (source.includes('restart-suppressed')) return 'restart_suppressed';
  return 'unknown';
};

export const getCrashLastScreen = (event: AppErrorEvent): string | null => {
  const lastScreen = readTagOrContext(event, 'lastScreen');
  if (typeof lastScreen === 'string' && lastScreen.trim()) return lastScreen.trim();
  const screenOrPage = readTagOrContext(event, 'screenOrPage');
  if (typeof screenOrPage === 'string' && screenOrPage.trim()) return screenOrPage.trim();
  return null;
};

export const isCrashLikeEvent = (event: AppErrorEvent): boolean => {
  if (readTagOrContext(event, 'isCrash') === true) return true;
  const kind = getCrashEventKind(event);
  if (kind === 'js_fatal' || kind === 'dirty_exit' || kind === 'restart_app') return true;
  const source = event.source ?? '';
  if (CRASH_SOURCES.has(source) && event.level === 'error') return true;
  return source.startsWith('mobile.crash.');
};

export const buildCrashFingerprint = (event: AppErrorEvent): string => {
  const kind = getCrashEventKind(event);
  const lastScreen = getCrashLastScreen(event) ?? 'unknown_screen';
  const source = event.source ?? 'unknown';
  const message = (event.message || 'Unknown crash').toLowerCase().replace(/\s+/g, ' ').trim();
  return `${kind}|${source}|${lastScreen}|${message}`;
};

export const aggregateCrashGroups = (events: AppErrorEvent[]): CrashGroup[] => {
  const groups = new Map<string, CrashGroup>();

  events.filter(isCrashLikeEvent).forEach((event) => {
    const fingerprint = buildCrashFingerprint(event);
    const existing = groups.get(fingerprint);
    const lastScreen = getCrashLastScreen(event);
    const eventKind = getCrashEventKind(event);
    if (!existing) {
      groups.set(fingerprint, {
        fingerprint,
        eventKind,
        message: event.message,
        lastScreen,
        source: event.source ?? null,
        platforms: event.platform ? [event.platform] : [],
        versions: event.app_version ? [event.app_version] : [],
        count: 1,
        lastSeenAt: event.created_at,
        firstSeenAt: event.created_at,
        sample: event,
      });
      return;
    }

    existing.count += 1;
    if (event.created_at > existing.lastSeenAt) {
      existing.lastSeenAt = event.created_at;
      existing.sample = event;
      existing.message = event.message;
    }
    if (event.created_at < existing.firstSeenAt) {
      existing.firstSeenAt = event.created_at;
    }
    if (event.platform && !existing.platforms.includes(event.platform)) {
      existing.platforms.push(event.platform);
    }
    if (event.app_version && !existing.versions.includes(event.app_version)) {
      existing.versions.push(event.app_version);
    }
  });

  return Array.from(groups.values()).sort((a, b) => b.lastSeenAt.localeCompare(a.lastSeenAt));
};

export const summarizeCrashCounts = (events: AppErrorEvent[], sinceMs = 24 * 60 * 60 * 1000) => {
  const cutoff = Date.now() - sinceMs;
  const recent = events.filter((event) => new Date(event.created_at).getTime() >= cutoff);
  return recent.reduce(
    (acc, event) => {
      const kind = getCrashEventKind(event);
      if (kind === 'js_fatal') acc.fatalJs += 1;
      if (kind === 'dirty_exit') acc.dirtyExits += 1;
      if (kind === 'restart_app') acc.restarts += 1;
      acc.total += 1;
      return acc;
    },
    { total: 0, fatalJs: 0, dirtyExits: 0, restarts: 0 },
  );
};
